import { useState, useEffect, useMemo } from "react";
import { API } from "../App";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Clock, MapPin, Loader2, Heart, ShieldCheck,
} from "lucide-react";

// US state name lookup — shared with backend normalizer
const US_STATE_CODE_TO_NAME = {
  AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",
  CT:"Connecticut",DE:"Delaware",DC:"District of Columbia",FL:"Florida",GA:"Georgia",
  HI:"Hawaii",ID:"Idaho",IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",
  LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",
  MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",
  NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",
  OH:"Ohio",OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",
  SD:"South Dakota",TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",
  WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming",PR:"Puerto Rico",
};

const NAME_TO_CODE = Object.fromEntries(
  Object.entries(US_STATE_CODE_TO_NAME).map(([c, n]) => [n.toLowerCase(), c])
);

function normalizeState(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (s.length === 2 && US_STATE_CODE_TO_NAME[s.toUpperCase()]) return s.toUpperCase();
  return NAME_TO_CODE[s.toLowerCase()] || null;
}

// ─── Waitlist Form ─────────────────────────────────────────────────────
const WaitlistForm = ({ intake, listType, stateCode, programs, onSuccess, onBack }) => {
  const [form, setForm] = useState({
    name: `${intake.first_name || ""} ${intake.last_name || ""}`.trim(),
    email: intake.email || "",
    zip_code: intake.zip_code || "",
    phone: intake.phone || "",
    program_of_interest: "",
    ideal_timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/state-waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          state: stateCode,
          list_type: listType,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onSuccess();
    } catch (e) {
      toast.error(e.message || "Sign-up failed — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="state-waitlist-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-slate-700">Full Name *</Label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} className="input-light" data-testid="waitlist-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-700">Email *</Label>
          <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} className="input-light" data-testid="waitlist-email" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-700">ZIP Code</Label>
          <Input value={form.zip_code} onChange={e => set("zip_code", e.target.value)} className="input-light" maxLength={5} data-testid="waitlist-zip" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-700">Phone</Label>
          <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="input-light" data-testid="waitlist-phone" />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-slate-700">Program of Interest</Label>
          <Select value={form.program_of_interest} onValueChange={v => set("program_of_interest", v)}>
            <SelectTrigger className="input-light" data-testid="waitlist-program-trigger"><SelectValue placeholder="Pick a program (optional)" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="undecided">I’m still deciding</SelectItem>
              {programs.filter(p => p.is_active).map(p => (
                <SelectItem key={p.program_id} value={p.name}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-slate-700">Ideal timeline</Label>
          <Select value={form.ideal_timeline} onValueChange={v => set("ideal_timeline", v)}>
            <SelectTrigger className="input-light" data-testid="waitlist-timeline-trigger"><SelectValue placeholder="When are you hoping to start?" /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="asap">As soon as possible</SelectItem>
              <SelectItem value="3_months">Within 3 months</SelectItem>
              <SelectItem value="6_months">Within 6 months</SelectItem>
              <SelectItem value="1_year">Within the next year</SelectItem>
              <SelectItem value="flexible">I’m flexible — keep me posted</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap justify-between gap-3 pt-2">
        <Button variant="outline" onClick={onBack} className="btn-outline gap-2" data-testid="waitlist-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <Button onClick={submit} disabled={submitting} className="btn-primary gap-2" data-testid="waitlist-submit-btn">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Join Waitlist <ArrowRight className="w-4 h-4" /></>}
        </Button>
      </div>
    </div>
  );
};

// ─── Main Routing Screen ───────────────────────────────────────────────
const StateRoutingScreen = ({ intake, programs, onContinueToEligibility, onBackToIntake }) => {
  const [config, setConfig] = useState(null);
  const [submitted, setSubmitted] = useState(null); // {list_type}

  useEffect(() => {
    fetch(`${API}/public/state-enrollment-config`)
      .then(r => r.json())
      .then(setConfig)
      .catch(() => {
        const ready = ["IL", "OH", "MA", "CA", "IN", "CT"];
        const comingSoon = Object.keys(US_STATE_CODE_TO_NAME).filter(c => !ready.includes(c));
        setConfig({ ready, coming_soon: comingSoon });
      });
  }, []);

  const stateCode = useMemo(() => normalizeState(intake.state), [intake.state]);
  const stateName = stateCode ? US_STATE_CODE_TO_NAME[stateCode] : (intake.state || "your state");

  // Determine routing bucket
  const bucket = useMemo(() => {
    if (!config || !stateCode) return null;
    if (config.ready.includes(stateCode)) return "ready";
    if (config.coming_soon.includes(stateCode)) return "coming_soon";
    return "closed";
  }, [config, stateCode]);

  if (!config) {
    return (
      <Card className="card-light">
        <CardContent className="pt-10 pb-10 flex items-center justify-center text-slate-500 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking your state…
        </CardContent>
      </Card>
    );
  }

  // Unrecognized state → treat as closed
  if (!stateCode) {
    return (
      <Card className="card-light overflow-hidden" data-testid="state-routing-unknown">
        <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white space-y-0">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-heading font-bold">We couldn’t recognize that state</h2>
              <p className="text-white/85 text-sm">Go back and double-check the state on your intake form.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <Button onClick={onBackToIntake} variant="outline" className="btn-outline gap-2" data-testid="routing-back-to-intake">
            <ArrowLeft className="w-4 h-4" /> Back to Intake
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Post-submit confirmation (waitlist branches)
  if (submitted) {
    return (
      <Card className="card-light overflow-hidden" data-testid="waitlist-confirmation">
        <CardHeader className="bg-gradient-to-br from-brand-navy to-[#152C6E] p-8 text-white space-y-0">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center shrink-0">
              <Heart className="w-6 h-6 text-brand-gold" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold">You’re on the list!</h2>
              <p className="text-white/85 mt-1">
                {submitted.list_type === "priority"
                  ? `We'll email you the moment ${stateName}'s application opens.`
                  : `As soon as Aspiritech Academy opens in ${stateName}, you'll be the first to know.`}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-3">
          <p className="text-sm text-slate-600">
            In the meantime, follow us on LinkedIn or check out our public programs — many of our
            community resources are available no matter where you live.
          </p>
          <p className="text-xs text-slate-500">
            Tip: add <span className="font-semibold">academy@aspiritech.org</span> to your contacts so our email doesn’t end up in spam.
          </p>
        </CardContent>
      </Card>
    );
  }

  // READY: continue to eligibility
  if (bucket === "ready") {
    return (
      <Card className="card-light overflow-hidden" data-testid="state-routing-ready">
        <CardHeader className="bg-gradient-to-br from-brand-navy to-[#152C6E] p-6 text-white space-y-0">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6 text-brand-gold" />
            </div>
            <div>
              <p className="text-brand-gold text-xs uppercase tracking-[0.2em] font-bold mb-1">Open in your state</p>
              <h2 className="text-2xl font-heading font-bold">Welcome, {stateName} resident!</h2>
              <p className="text-white/85 text-sm mt-1">
                You’re on the standard application path. Next, we’ll collect the eligibility
                documentation needed for federal workforce reporting.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="flex items-center gap-1.5 text-brand-navy font-semibold">
                <ShieldCheck className="w-4 h-4" /> Eligibility
              </div>
              <p className="text-slate-600 text-xs mt-0.5">~10 minutes · federal WIOA questions</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="flex items-center gap-1.5 text-brand-navy font-semibold">
                <Clock className="w-4 h-4" /> Program Readiness
              </div>
              <p className="text-slate-600 text-xs mt-0.5">~5 minutes · short scenario questions</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="flex items-center gap-1.5 text-brand-navy font-semibold">
                <Heart className="w-4 h-4" /> Program of Interest
              </div>
              <p className="text-slate-600 text-xs mt-0.5">Pick your top program (admin reviews)</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-between gap-3 pt-2">
            <Button variant="outline" onClick={onBackToIntake} className="btn-outline gap-2" data-testid="routing-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            <Button onClick={onContinueToEligibility} className="btn-primary gap-2" data-testid="routing-continue-btn">
              Continue to Eligibility <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // COMING_SOON: priority waitlist
  if (bucket === "coming_soon") {
    return (
      <Card className="card-light overflow-hidden" data-testid="state-routing-coming-soon">
        <CardHeader className="bg-gradient-to-br from-[#1E3A8A] via-[#3B5BB6] to-[#FFD507]/40 p-6 text-white space-y-0">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center shrink-0">
              <MapPin className="w-6 h-6 text-brand-gold" />
            </div>
            <div>
              <p className="text-brand-gold text-xs uppercase tracking-[0.2em] font-bold mb-1">Coming Soon</p>
              <h2 className="text-2xl font-heading font-bold">We’re coming soon to {stateName}!</h2>
              <p className="text-white/90 text-sm mt-1">
                Aspiritech Academy isn’t fully approved to register residents of {stateName} just
                yet — but we’re working on it! Add your details below and we’ll be in touch the
                moment we’re able to welcome you in.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <WaitlistForm
            intake={intake}
            listType="priority"
            stateCode={stateCode}
            programs={programs}
            onSuccess={() => setSubmitted({ list_type: "priority" })}
            onBack={onBackToIntake}
          />
        </CardContent>
      </Card>
    );
  }

  // CLOSED: general waitlist
  return (
    <Card className="card-light overflow-hidden" data-testid="state-routing-closed">
      <CardHeader className="bg-gradient-to-br from-slate-700 to-brand-navy p-6 text-white space-y-0">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center shrink-0">
            <Heart className="w-6 h-6 text-brand-gold" />
          </div>
          <div>
            <p className="text-brand-gold text-xs uppercase tracking-[0.2em] font-bold mb-1">Coming to {stateName} one day!</p>
            <h2 className="text-2xl font-heading font-bold">We’re not yet open in {stateName}</h2>
            <p className="text-white/85 text-sm mt-1">
              Aspiritech Academy is still expanding state-by-state. Sign up for our waitlist and we
              will be in touch with you as soon as we open in your area!
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <WaitlistForm
          intake={intake}
          listType="general"
          stateCode={stateCode}
          programs={programs}
          onSuccess={() => setSubmitted({ list_type: "general" })}
          onBack={onBackToIntake}
        />
      </CardContent>
    </Card>
  );
};

export default StateRoutingScreen;
export { US_STATE_CODE_TO_NAME };
